import type { MonthlyCadence, WeekendAdjustment } from "@fomofi/shared";
import apiClient from "./client";
import type { ForecastPeriod } from "@/data/mockDashboard";

export interface ScenarioProjection {
  expected: string;
  optimistic: string;
  pessimistic: string;
}

export interface DailyProjectionEntry {
  date: string;
  income: number;
  expenses: number;
  balance: number;
}

export interface ForecastData {
  id: number;
  start_date: string;
  end_date: string;
  projected_income: ScenarioProjection;
  projected_expenses: ScenarioProjection;
  projected_balance: ScenarioProjection;
  daily_projections: {
    expected: DailyProjectionEntry[];
    optimistic?: DailyProjectionEntry[];
    pessimistic?: DailyProjectionEntry[];
  };
  risk_alerts: unknown[];
  generated_at: string;
}

/**
 * THE recurring-transaction cadence vocabulary — exactly the server's
 * `RecurringTransaction.frequency` choices.
 *
 * BEN-141 4b item 7. This used to declare `"bi-weekly"` (hyphenated), which the
 * server REJECTS: that spelling belongs to `apps.debts.DebtPlan`, a different model
 * with its own vocabulary. Nothing caught it because nothing wrote this field — the
 * read path only ever received the server's own `"biweekly"`. Two live consequences:
 *
 *   - RecurringTransactions.tsx offered "bi-weekly" in a Select and PATCHed it, so a
 *     user picking Bi-weekly got "Failed to save changes. Please try again."
 *   - Item 7's fix (send `frequency` from EditIncomeForm) would have shipped a silent
 *     400 had it simply cast to this type.
 *
 * `CreateRecurringData` and `UpdateRecurringData` now share this ONE type, so create
 * and update cannot drift apart again.
 */
export type RecurringFrequency = MonthlyCadence | "semimonthly";

export interface RecurringTransaction {
  id: number;
  merchant_name: string;
  amount: string;
  frequency: RecurringFrequency;
  /** CAL-001: the two calendar days of a "semimonthly" stream (31 = last day),
   *  `[]` for every other cadence. */
  semimonthly_days?: number[];
  next_expected_date: string;
  is_income: boolean;
  is_active: boolean;
  /** AN-69: off-weekend projection. A paycheck's is detected from its deposits and
   *  editable on the paycheck (Paul, 2026-11-03). Optional on the wire for older
   *  responses; absent reads as "none". */
  weekend_adjustment?: WeekendAdjustment;
  parent_category: string;
  /** Plaid stream id, or null for a row the user entered by hand (BEN-141 N7,
   *  #1046). Non-null rows are sync-managed: the backend rejects DELETE on them
   *  because the next sync would recreate the row, so they are deactivated
   *  instead. Mobile has read this since #496 and used it to label a row's
   *  provenance; web declared nothing, so its list could not tell a user which
   *  of their bills the app found for them and which they typed in. */
  stream_id: string | null;
  /** Plaid's confidence in the detected stream, as a decimal string ("0.98"),
   *  or null on a manual row. */
  confidence_score: string | null;
  /** Plaid's own last occurrence date for the stream. It moves only when Plaid
   *  recomputes the stream, which is why `last_seen_at` exists beside it. */
  last_date: string | null;
  /** Date of the newest matching transaction WE ingested (#495). On the webhook
   *  path a live subscription can look a month stale by `last_date` alone, so a
   *  freshness surface reads the newer of the two. Optional on the wire for a
   *  frontend deployed ahead of the backend. */
  last_seen_at?: string | null;
  /** User-confirmed gross pay per cycle for an income stream, or null when they
   *  have not been asked (BEN-141 P31). Read back here so the row the
   *  confirm-paycheck step PATCHes can be re-rendered from its own response
   *  rather than a second fetch of the paycheck endpoint. `amount` remains the
   *  net deposit. */
  gross_amount: string | null;
}

export interface UpdateRecurringData {
  merchant_name?: string;
  /** A paycheck's weekend setting (Paul, 2026-11-03); the server records it as the
   *  user's, so the next sync's detection leaves it alone (#1383). */
  weekend_adjustment?: WeekendAdjustment;
  amount?: string;
  frequency?: RecurringFrequency;
  /** Required with `frequency: "semimonthly"` (the server rejects it otherwise);
   *  the fourth field this type would otherwise have dropped silently. */
  semimonthly_days?: number[];
  /** ISO `YYYY-MM-DD`. The stream's cadence ANCHOR — every future occurrence is
   *  stepped from it, so this is not a one-off correction to the next date.
   *
   *  Added in BEN-141 3b. `RecurringTransactionUpdateSerializer` has always accepted it
   *  (`backend/apps/forecasting/serializers.py:77-84`); it was simply missing from this
   *  type, which is the same omission item 7 fixed for `frequency` — and it fails the
   *  same silent way, since an un-typed key is dropped before the request is built. */
  next_expected_date?: string;
  /** Which budget bucket this bill is counted in — "needs" or "wants".
   *
   *  BEN-141 item 1b, and the THIRD time this exact omission has been found on
   *  this one type: `frequency` (item 7) and `next_expected_date` (3b) were both
   *  accepted by `RecurringTransactionUpdateSerializer` and both missing here.
   *  The failure is silent every time — an un-typed key is dropped before the
   *  request is built, so the control saves, returns 200, and changes nothing. */
  parent_category?: "needs" | "wants";
  is_active?: boolean;
  /** Gross pay per cycle for an INCOME stream, as a decimal string. The
   *  confirm-paycheck step's deductions answer (BEN-141 P31): the backend keeps
   *  `amount` as the net deposit and derives deductions as the difference, so a
   *  value below `amount` is rejected with a 400 on `gross_amount`. */
  gross_amount?: string;
}

export type BalanceHealth = "healthy" | "caution" | "warning" | "danger";

export interface CalendarDay {
  date: string; // YYYY-MM-DD
  income: number;
  expenses: number;
  balance: number;
  balance_health: BalanceHealth;
}

export interface CalendarMonthSummary {
  total_income: number;
  total_expenses: number;
  net: number;
  lowest_balance: number;
}

export interface CalendarResponse {
  month: number;
  year: number;
  days: CalendarDay[];
  month_summary: CalendarMonthSummary;
  /**
   * Whether the requested month overlaps the forecast horizon at all
   * (BEN-141 item 6).
   *
   * Before C6 the engine projected 90 days, so navigating four months forward returned
   * `days: []` with a zeroed summary — and the page rendered "$0 income, $0
   * expenses, lowest balance $0" for a month it had no data for. This is the one
   * distinction the client cannot derive: an empty month INSIDE the horizon has
   * genuinely no cash events and its zeros are true; an empty month OUTSIDE it is
   * unknown. Optional so a frontend that deploys ahead of the backend treats a
   * missing field as covered, i.e. exactly today's behaviour, rather than
   * declaring every month unknown.
   */
  covered?: boolean;
  /** Covered, but the month runs past the end of the projection (item 8). */
  partially_covered?: boolean;
  /** The whole month is before the projection starts: a past month (CAL-009). */
  before_horizon?: boolean;
  projected_through?: string | null;
  horizon_start?: string | null;
  /** Last date the projection covers — used to name the limit, not shrug at it. */
  horizon_end?: string | null;
}

export async function getForecast(): Promise<ForecastData | null> {
  const { data } = await apiClient.get<ForecastData>("/forecasting/");
  return data;
}

export async function generateForecast(): Promise<ForecastData> {
  const { data } = await apiClient.post<ForecastData>("/forecasting/generate/");
  return data;
}

export async function getRecurringTransactions(): Promise<RecurringTransaction[]> {
  // include_inactive: the management page must list toggled-off rows so the
  // active toggle can turn them back ON — without it, deactivating a row made
  // it unreachable (one-way door). Forecast-facing reads stay active-only.
  const { data } = await apiClient.get<RecurringTransaction[]>(
    "/forecasting/recurring/?include_inactive=true",
  );
  return data;
}

/** Income (b): "Still expecting this?" → Keep. Counts until the next cycle passes
 *  without a deposit. `undo` clears it. */
export async function keepIncomeStream(id: number, undo = false): Promise<void> {
  if (undo) await apiClient.delete(`/forecasting/recurring/${id}/keep-income/`);
  else await apiClient.post(`/forecasting/recurring/${id}/keep-income/`);
}

export async function updateRecurringTransaction(
  id: number,
  updateData: UpdateRecurringData,
): Promise<RecurringTransaction> {
  const { data } = await apiClient.patch<RecurringTransaction>(
    `/forecasting/recurring/${id}/`,
    updateData,
  );
  return data;
}

/** Backend-valid recurring cadences (RecurringTransaction.frequency choices). */
/** @deprecated Alias of {@link RecurringFrequency}; create and update share one
 *  vocabulary now. Kept so existing imports keep compiling. */
export type CreateRecurringFrequency = RecurringFrequency;

export interface CreateRecurringData {
  merchant_name: string;
  amount: string; // positive magnitude; direction comes from is_income
  frequency: CreateRecurringFrequency;
  /** Required with `frequency: "semimonthly"` (#1385), as on update. */
  semimonthly_days?: number[];
  parent_category: string; // "income" for income, else needs/wants/savings
  is_income: boolean;
  // next_expected_date is optional — the backend defaults it from frequency.
  next_expected_date?: string;
}

/** Create a manual recurring income/bill (POST). */
export async function createRecurringTransaction(
  payload: CreateRecurringData,
): Promise<RecurringTransaction> {
  const { data } = await apiClient.post<RecurringTransaction>("/forecasting/recurring/", payload);
  return data;
}

/** Delete a recurring row. Backend 403s on Plaid-detected streams (stream_id set). */
export async function deleteRecurringTransaction(id: number): Promise<void> {
  await apiClient.delete(`/forecasting/recurring/${id}/`);
}

export async function getCalendar(month: number, year: number): Promise<CalendarResponse | null> {
  const { data } = await apiClient.get<CalendarResponse>(
    `/forecasting/calendar/?month=${month}&year=${year}`,
  );
  return data;
}

/** Backend pay-period series item (snake_case mirror of PayPeriodSeriesItemSerializer).
 *  needs/wants/savings are a true partition of income (sum to income at 50/30/20). */
interface PayPeriodSeriesItem {
  label: string;
  start_date?: string;
  end_date?: string;
  income: number;
  needs: number;
  wants: number;
  savings: number;
  /** Backend-declared income base (BEN-141 4a D5). Optional for backward compat. */
  basis?: string;
  basis_label?: string;
}

/** Next N real pay-period projections, adapted to the chart's shape. */
export async function getForecastPeriods(count = 6): Promise<ForecastPeriod[]> {
  const { data } = await apiClient.get<PayPeriodSeriesItem[]>(
    `/forecasting/periods/?count=${count}`,
  );
  return data.map((p) => ({
    label: p.label,
    startDate: p.start_date,
    endDate: p.end_date,
    income: p.income,
    needs: p.needs,
    wants: p.wants,
    savings: p.savings,
    basisLabel: p.basis_label,
  }));
}
